import { accents, type AccentName, type ThemeName } from '@cottonray/design-tokens'
import { accountLanguageOf, type AccountLanguage } from './labels'

/**
 * 앱이 계정 화면을 열 때 주소로 넘겨 주는 것들 — **문의 화면(`support/params.ts`)과 같은
 * 규칙**이다.
 *
 * ## 여기에 개인정보가 없다
 *
 * 문의 화면은 회신 주소를 프래그먼트로 받았다. 계정 화면은 그마저 없다 — 이름·이메일·
 * 아이디는 전부 **다리로** 오간다(`bridge.ts`). 주소에 실으면 nginx 접근 로그와 Loki 에
 * 남는데, 그건 로그인한 사람의 신원이라 남길 이유가 없다.
 *
 * 그래서 여기 있는 값은 전부 "어떻게 그릴까"뿐이다: 어느 서비스인지, 어떤 테마·언어인지.
 */
export interface AccountContext {
  /** 서비스명. 화면 문구가 아니라 방침 링크·로그용이다 */
  service: string
  /** 앱 버전 — 화면 아래 작은 글씨 */
  appVersion?: string
  language: AccountLanguage
  /** 안 넘기면 시스템 설정(prefers-color-scheme)을 따른다 */
  theme?: ThemeName
  /** 디자인 토큰의 액센트 이름 — 앱과 **같은 값**이 나온다 */
  accent?: AccentName
  /** 토큰에 없는 색일 때의 `#rrggbb` */
  accentHex?: string
  /**
   * 앱 안 웹뷰에 담겨 있나. 켜지면 **페이지가 자기 제목을 안 그린다** — 그 자리는 앱
   * 상단바가 이미 차지하고 있어 "내 계정"이 두 번 나온다 (문의 화면과 같은 판단).
   */
  embed: boolean
}

/** 기본 서비스. 주소를 손으로 열어 본 경우에도 화면은 뜬다 */
const DEFAULT_SERVICE = 'cottonray'

const SERVICE_SHAPE = /^[a-z0-9][a-z0-9-]*$/
const HEX_SHAPE = /^#?([0-9a-f]{6})$/i

/**
 * 주소를 읽는다. **아무것도 안 넘어와도 화면은 뜬다** — 잘못된 값은 무시하고 기본값으로
 * 내려간다 (문의 화면과 같은 규칙: 파라미터 하나가 틀렸다고 화면을 막지 않는다).
 */
export function readAccountContext(search: string, navigatorLanguage?: string): AccountContext {
  const query = new URLSearchParams(search)

  const service = text(query.get('service'), 40)
  const appVersion = text(query.get('appVersion'), 32)

  return {
    service: service !== undefined && SERVICE_SHAPE.test(service) ? service : DEFAULT_SERVICE,
    ...optional('appVersion', appVersion),
    // 앱이 고른 언어가 우선 — 앱 UI 가 영어인데 계정만 한국어로 뜨면 안 된다
    language: accountLanguageOf(query.get('lang') ?? navigatorLanguage),
    ...readTheme(query.get('theme'), query.get('accent')),
    embed: query.get('embed') === '1',
  }
}

/**
 * 테마. **이름으로 받는 쪽이 정확하다** — 앱이 `ThemeProvider accent="violet"` 을 쓰면
 * 웹도 같은 토큰을 읽어 값이 한 곳에서 나온다 (`support/params.ts` 와 같은 규칙).
 */
function readTheme(
  theme: string | null,
  accent: string | null,
): Pick<AccountContext, 'theme' | 'accent' | 'accentHex'> {
  const name = theme?.trim().toLowerCase()
  const scheme: ThemeName | undefined = name === 'dark' || name === 'light' ? name : undefined

  const raw = accent?.trim() ?? ''
  if (raw in accents) {
    return { ...optional('theme', scheme), accent: raw as AccentName }
  }
  const digits = HEX_SHAPE.exec(raw)?.[1]
  if (digits !== undefined) {
    return { ...optional('theme', scheme), accentHex: `#${digits.toLowerCase()}` }
  }
  return optional('theme', scheme)
}

function optional<K extends string, V>(key: K, value: V | undefined): { [P in K]?: V } {
  return (value === undefined ? {} : { [key]: value }) as { [P in K]?: V }
}

function text(raw: string | null, max: number): string | undefined {
  const trimmed = raw?.trim()
  if (trimmed === undefined || trimmed === '') return undefined
  return trimmed.slice(0, max)
}

/** 주소는 이 페이지가 사는 동안 바뀌지 않으니 한 번만 읽는다 */
let cached: AccountContext | undefined

export function accountContext(): AccountContext {
  cached ??= readAccountContext(window.location.search, navigator.language)
  return cached
}
